import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  X, 
  User,
  Clock,
  Shield,
  AlertCircle,
  FileText,
  Package
} from 'lucide-react';
import { formatDate } from '../../lib/utils';

interface Client {
  id: string;
  username: string;
  password: string;
  member_id: number;
  package_id?: number;
  exp_date?: number;
  max_connections: number;
  is_trial: number;
  system: number;
  enabled: number;
  p2p_id?: number;
  p2p_status?: string;
  reseller_notes?: string;
  created_at: string;
  updated_at: string;
  member?: {
    id: number;
    username: string;
    email: string;
    status: number;
  };
  package?: {
    id: number;
    package_name: string;
    official_credits: number;
    official_duration: number;
  };
}

interface ClientViewModalProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
}

const ClientViewModal: React.FC<ClientViewModalProps> = ({
  client,
  isOpen,
  onClose
}) => {
  if (!isOpen || !client) return null;

  const isExpired = (expDate: number) => {
    if (!expDate) return false;
    return expDate < Math.floor(Date.now() / 1000);
  };

  const getDaysUntilExpiration = (expDate: number) => {
    if (!expDate) return null;
    const now = Math.floor(Date.now() / 1000);
    const diff = expDate - now;
    return Math.ceil(diff / (24 * 60 * 60));
  };

  const isIptvActive = client.enabled === 1 && !isExpired(client.exp_date || 0);
  const isP2pActive = client.system === 2 && client.p2p_status && String(client.p2p_status) === '1' && !isExpired(client.exp_date || 0);
  const isActive = isIptvActive || isP2pActive;
  const daysUntilExp = getDaysUntilExpiration(client.exp_date || 0);
  const expired = isExpired(client.exp_date || 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 p-0 sm:p-4 overflow-hidden modal-overlay-client flex items-center justify-center">
      <div className="modal-mobile-medium modal-client-details bg-white rounded-none sm:rounded-lg shadow-xl max-h-[100vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden flex flex-col min-w-0">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Detalhes do Cliente</h2>
            <p className="text-sm sm:text-base text-gray-600">Informações completas da linha</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="flex-shrink-0 ml-2 min-h-[44px] min-w-[44px]">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          {/* Informações Básicas - blocos alinhados para mobile */}
          <Card className="min-w-0">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <User className="h-5 w-5 flex-shrink-0" />
                Informações Básicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0 min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 sm:gap-4 min-w-0">
                <div className="mobile-detail-block sm:border-0">
                  <label className="text-sm font-medium text-gray-500">Nome de usuário</label>
                  <p className="text-base sm:text-lg font-semibold text-gray-900 break-all">{client.username}</p>
                </div>
                <div className="mobile-detail-block sm:border-0">
                  <label className="text-sm font-medium text-gray-500">Senha</label>
                  <p className="text-base sm:text-lg font-mono text-gray-900 break-all">{client.password}</p>
                </div>
                <div className="mobile-detail-block sm:border-0">
                  <label className="text-sm font-medium text-gray-500">Conexões máximas</label>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">{client.max_connections}</p>
                </div>
              </div>
              <CardTitle className="flex items-center gap-1">
                <Shield className="h-5 w-5" />
                Status e Sistema
              </CardTitle>
              <div className="flex grid-cols-3 min-w-0">
                <div>
                 
                    <Badge className={ `m-2 ${
                      isActive 
                        ? 'bg-green-100 text-green-800 border-green-200' 
                        : 'bg-red-100 text-red-800 border-red-200'
                    }`}>
                      {isActive ? (isP2pActive ? 'Ativo P2P' : 'Ativo IPTV') : 'Inativo'}
                    </Badge>
                
                </div>
                <div>
                    <Badge className={` m-2 ${
                      client.system === 1 
                        ? 'bg-blue-100 text-blue-800 border-blue-200' 
                        : 'bg-purple-100 text-purple-800 border-purple-200'
                    }`}>
                      {client.system === 1 ? 'IPTV' : 'P2P'}
                    </Badge>
                </div>
                <div>
                    <Badge className={` m-2 ${
                      client.is_trial === 1 
                        ? 'bg-purple-100 text-purple-800 border-purple-200' 
                        : 'bg-blue-100 text-blue-800 border-blue-200'
                    }`}>
                      {client.is_trial === 1 ? 'Trial' : 'Normal'}
                    </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informações de Expiração */}
          <Card className="min-w-0">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Clock className="h-5 w-5 flex-shrink-0" />
                Informações de Expiração
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0 min-w-0">
              {client.exp_date ? (
                <div className="space-y-0">
                  <div className="mobile-detail-block sm:border-0">
                    <label className="text-sm font-medium text-gray-500">Data de expiração</label>
                    <p className="text-base sm:text-lg font-semibold text-gray-900">
                      {formatDate(new Date(client.exp_date * 1000).toISOString())}
                    </p>
                  </div>
                  {daysUntilExp !== null && (
                    <div className="mobile-detail-block sm:border-0">
                      <label className="text-sm font-medium text-gray-500">Dias restantes</label>
                      <div className="flex items-center gap-2">
                        {expired ? (
                          <div className="flex items-center gap-2 text-red-600">
                            <AlertCircle className="h-4 w-4" />
                            <span className="font-semibold">Expirado</span>
                          </div>
                        ) : (
                          <div className={`flex items-center gap-2 ${
                            daysUntilExp <= 7 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            <Clock className="h-4 w-4" />
                            <span className="font-semibold">
                              {daysUntilExp} {daysUntilExp === 1 ? 'dia' : 'dias'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Nenhuma data de expiração definida</p>
              )}
            </CardContent>
          </Card>

          {/* Pacote */}
          {client.package && (
            <Card className="min-w-0">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Package className="h-5 w-5 flex-shrink-0" />
                  Pacote
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-0 min-w-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 sm:gap-4 min-w-0">
                  <div className="mobile-detail-block sm:border-0">
                    <label className="text-sm font-medium text-gray-500">Nome do pacote</label>
                    <p className="text-base sm:text-lg font-semibold text-gray-900 break-words">{client.package.package_name}</p>
                  </div>
                  <div className="mobile-detail-block sm:border-0">
                    <label className="text-sm font-medium text-gray-500">Valor</label>
                    <p className="text-base sm:text-lg font-semibold text-gray-900">Crédito {client.package.official_credits}</p>
                  </div>
                  <div className="mobile-detail-block sm:border-0">
                    <label className="text-sm font-medium text-gray-500">Duração (mês/meses)</label>
                    <p className="text-base sm:text-lg font-semibold text-gray-900">{client.package.official_duration}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Proprietário */}
          {client.member && (
            <Card className="min-w-0">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <User className="h-5 w-5 flex-shrink-0" />
                  Proprietário
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-0 min-w-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 sm:gap-4 min-w-0">
                  <div className="mobile-detail-block sm:border-0">
                    <label className="text-sm font-medium text-gray-500">Username</label>
                    <p className="text-base sm:text-lg font-semibold text-gray-900 break-all">{client.member.username}</p>
                  </div>
                  <div className="mobile-detail-block sm:border-0">
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-base sm:text-lg font-semibold text-gray-900 break-all">{client.member.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notas do Revendedor */}
          <Card className="min-w-0">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <FileText className="h-5 w-5 flex-shrink-0" />
                Notas do Revendedor
              </CardTitle>
            </CardHeader>
            <CardContent className="min-w-0">
              {client.reseller_notes && client.reseller_notes.trim() ? (
                <p className="text-sm sm:text-base text-gray-800 whitespace-pre-wrap break-words leading-relaxed bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  {client.reseller_notes}
                </p>
              ) : (
                <p className="text-sm text-gray-400 italic">Nenhuma nota registrada para este cliente.</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3 p-4 sm:p-6 border-t bg-gray-50 flex-shrink-0 min-w-0">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto min-h-[44px]">
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientViewModal;
